import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
} from "@mui/material";
import { GrantUserRoles } from "../../redux/actions/GrantUserRolesAction";

const GrantUserRolesModal = ({ open, onClose, userData, roles }) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.grantUserRoles.status);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const loading = status === "loading";

  const assignedRoleIds = (userData?.rols || []).map(
    (role) => role.idrole ?? role.idRole
  );

  const availableRoles = roles.filter(
    (role) => !assignedRoleIds.includes(role.idRole)
  );

  useEffect(() => {
    if (open) {
      setSelectedRoleIds([]);
      setMessage({ type: "", text: "" });
    }
  }, [open, userData]);

  const handleRoleToggle = (roleId) => {
    if (assignedRoleIds.includes(roleId)) {
      return;
    }

    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  const isFormValid = () =>
    userData?.iduser != null && selectedRoleIds.length > 0;

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    setMessage({ type: "", text: "" });

    try {
      const result = await dispatch(
        GrantUserRoles(userData.iduser, selectedRoleIds)
      );

      if (result.success) {
        setMessage({ type: "success", text: result.message });
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch {
      setMessage({
        type: "error",
        text: "There was an error granting roles. Please try again.",
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Grant Roles</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label="User"
            value={userData?.userName ?? ""}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Current roles
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(userData?.rols || []).length > 0 ? (
              userData.rols.map((role) => (
                <Chip
                  key={role.idrole ?? role.idRole}
                  label={role.roleName}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No roles assigned
              </Typography>
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Add roles
          </Typography>
          <FormGroup>
            {availableRoles.map((role) => (
              <FormControlLabel
                key={role.idRole}
                control={
                  <Checkbox
                    checked={selectedRoleIds.includes(role.idRole)}
                    onChange={() => handleRoleToggle(role.idRole)}
                    disabled={loading}
                  />
                }
                label={role.roleName}
              />
            ))}
          </FormGroup>

          {roles.length === 0 && (
            <Alert severity="info">
              No active roles available for this country.
            </Alert>
          )}

          {roles.length > 0 && availableRoles.length === 0 && (
            <Alert severity="info">
              This user already has all available roles for this country.
            </Alert>
          )}

          {message.text && (
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{
            backgroundColor: "#4b4bfa",
            "&:hover": { backgroundColor: "#3e3ed9" },
            color: "#fff",
          }}
        >
          {loading ? "Saving..." : "Grant Roles"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrantUserRolesModal;
